import React from "react";
import { Spin } from "antd";
import { useGetSubQuoteProducts } from "../../hooks";
import { ISubQuoteProduct } from "../../types";
import { JSX } from "react/jsx-runtime";
import { formatDealerAmount } from "@/utils/currency";
import { IDealersCurrency } from "@/types";
import { getSubProductOptionType } from "./detail-shared/helpers";
import { OPTION_TYPE_EGRESS_COMPLAINT } from "@/features/assistant/constants";


interface WindowConfigurationDisplayProps {
  quoteProductId: string;
  subtotal?: number;
  dealersCurrency?: IDealersCurrency;
  exchangeRate?: number;
}

interface GroupedConfiguration {
  [categoryName: string]: ISubQuoteProduct[];
}

const parseValue = (value: string | undefined, fallbackName: string): string | JSX.Element => {
  if (!value) return fallbackName;

  try {
    // Try to parse as JSON
    const parsed = JSON.parse(value);

    // If it's an array, join the labels
    if (Array.isArray(parsed)) {
      const labels = parsed
        .map(item => {
          if (typeof item === 'object' && item !== null) {
            return item.label || item.name || item.value || String(item);
          }
          return String(item);
        })
        .filter(Boolean);

      return labels.length > 0 ? labels.join(', ') : fallbackName;
    }

    // If it's an object, check what type
    if (typeof parsed === 'object' && parsed !== null) {
      // Check if it's a dimension field (has multiple key-value pairs, no optionId)
      const isDimensionField = !parsed.optionId && !parsed.label;

      if (isDimensionField && Object.keys(parsed).length > 0) {
        // Display as list of key-value pairs with formatted keys
        return (
          <div className="space-y-1">
            {Object.entries(parsed).map(([key, val]) => {
              // Replace underscores with spaces
              const formattedKey = key.replace(/_/g, ' ');

              return (
                <div key={key} className="flex justify-between items-center text-xs">
                  <span className="text-left text-gray-600">{formattedKey}</span>
                  <span className="text-right text-gray-900 font-medium">{String(val)}</span>
                </div>
              );
            })}
          </div>
        );
      }

      // Regular answer object - get the label
      return parsed.label || parsed.name || parsed.value || fallbackName;
    }

    // If it's a primitive value, return it as string
    return String(parsed);
  } catch (e) {
    // If parsing fails, return the value as-is
    return value;
  }
};

export const WindowConfigurationDisplay: React.FC<WindowConfigurationDisplayProps> = ({
  quoteProductId,
  subtotal,
  dealersCurrency,
  exchangeRate,
}) => {
  const fmt = (amount: number) => formatDealerAmount(amount, dealersCurrency, exchangeRate);
  const { data: subProductsData, isLoading } = useGetSubQuoteProducts(quoteProductId);
  const subProducts = subProductsData?.value || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spin size="small" />
      </div>
    );
  }

  if (subProducts.length === 0) {
    return (
      <div className="text-gray-500 text-center py-4">
        No configuration details available
      </div>
    );
  }

  // Update subtotal amount if provided via props and filter out surge charges
  const updatedSubProducts = subProducts
    .filter(product => {
      // Filter out Surcharge and Additional Charges categories, plus the raw
      // egress Yes/No question — shown separately as the computed
      // Compliant/Non-Compliant badge, which reads worse alongside the raw
      // answer (e.g. badge "Non-Compliant" next to answer line "Yes").
      return product.dogma_categoryname !== 'Surcharge' &&
        product.dogma_categoryname !== 'Additional Charges' &&
        getSubProductOptionType(product) !== OPTION_TYPE_EGRESS_COMPLAINT;
    })
    .map(product => {
      if (product.dogma_categoryname === "Summary" && product.dogma_optionname === "Subtotal" && subtotal !== undefined) {
        return {
          ...product,
          dogma_amount: subtotal
        };
      }
      return product;
    });

  // Group sub products by category name
  const groupedByCategory: GroupedConfiguration = updatedSubProducts.reduce((acc, product) => {
    const category = product.dogma_categoryname || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {} as GroupedConfiguration);

  // Extract "Frame N" from dogma_questionjson → question.responsePath — mirrors
  // the PDF's getFrameFromProduct exactly, so on-screen order matches the PDF.
  const getFrameFromProduct = (product: ISubQuoteProduct): string | null => {
    try {
      const msg = JSON.parse(product.dogma_questionjson ?? '');
      const responsePath: string = msg?.question?.responsePath ?? '';
      const m = responsePath.match(/frame(\d+)$/i);
      if (m) return `Frame ${m[1]}`;
    } catch {
      // not JSON / no responsePath — not a per-frame question
    }
    return null;
  };

  // Group products into one row-group per question per frame — keyed by
  // (optionTypeName, step, frameLabel), exactly like the PDF — then sort
  // frame-major (non-frame first, then Frame 1, 2, …) so all of Frame 1's
  // answers render before Frame 2's, matching the PDF's row order instead of
  // raw API insertion order. Each returned entry's key still starts with the
  // plain optionName so existing per-row label logic needs no further changes.
  const groupByOptionName = (products: ISubQuoteProduct[]) => {
    const grouped = new Map<string, ISubQuoteProduct[]>();
    products.forEach(product => {
      const optionName = product.dogma_optionname || product.dogma_name;
      const step = product.dogma_step ?? 0;
      const frameLabel = getFrameFromProduct(product) ?? '';
      const key = `${optionName}\u0000${step}\u0000${frameLabel}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(product);
    });

    const sortedEntries = Array.from(grouped.entries()).sort(([keyA], [keyB]) => {
      const [, stepStrA, frameLabelA] = keyA.split('\u0000');
      const [, stepStrB, frameLabelB] = keyB.split('\u0000');
      const frameNumA = frameLabelA ? (parseInt(frameLabelA.replace(/\D/g, '')) || 999) : -1;
      const frameNumB = frameLabelB ? (parseInt(frameLabelB.replace(/\D/g, '')) || 999) : -1;
      if (frameNumA !== frameNumB) return frameNumA - frameNumB;
      return (parseInt(stepStrA ?? '0') || 0) - (parseInt(stepStrB ?? '0') || 0);
    });

    // React keys only need to be unique, not meaningful — the render code
    // reads the actual option name off each product, not off this key.
    const result: { [rowKey: string]: ISubQuoteProduct[] } = {};
    sortedEntries.forEach(([key, prods], idx) => {
      result[`${idx}\u0000${key}`] = prods;
    });
    return result;
  };

  // "Optional Extras" always last; every other category keeps its natural
  // (insertion) order — mirrors the mobile app's mapping exactly.
  const sortedCategories = Object.entries(groupedByCategory).sort(([a], [b]) => {
    if (a.toLowerCase() === 'optional extras') return 1;
    if (b.toLowerCase() === 'optional extras') return -1;
    return 0;
  });

  return (
    <div className="space-y-3">
      {sortedCategories.map(([categoryName, products]) => {
        const groupedByOption = groupByOptionName(products);
        return (
          <div key={categoryName} className="mb-3">
            <h5 className="text-xs text-left font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
              {categoryName}
            </h5>
            <div className="space-y-2 pl-1.5">
              {Object.entries(groupedByOption).map(([rowKey, optionProducts]) => {
                const isSummary = categoryName === "Summary";
                const firstProduct = optionProducts[0];
                if (!firstProduct) return null;
                // Each entry is already scoped to one frame (split out by
                // groupByOptionName) — prefix the label with it when present
                // so Frame 1's row reads "Frame 1 - X", Frame 2's "Frame 2 - X".
                const frameLabel = getFrameFromProduct(firstProduct) ?? '';

                // If single product in this option group
                if (optionProducts.length === 1) {
                  const product = firstProduct;
                  let displayValue: string | JSX.Element;
                  let displayLabel: string;

                  // Measurement/Measurements categories (e.g. Dimension) share one
                  // dogma_optionname across many distinct fields (width, height,
                  // shoulder height, ...) — dogma_name must stay the label there so a
                  // lone field doesn't lose its specific name to the generic group
                  // name. Every other category has one option per question, so
                  // dogma_optionname is the more meaningful label.
                  const isMeasurementCategory = ['measurement', 'measurements'].includes(
                    categoryName.toLowerCase(),
                  );
                  // "Cill Horn Size" alone hides WHICH field the value is for
                  // ("Each Side Size (mm)") — append it, scoped to just this option.
                  const optionPlusFieldLabel =
                    product.dogma_optionname === 'Cill Horn Size' && product.dogma_name
                      ? `${product.dogma_optionname} - ${product.dogma_name}`
                      : (product.dogma_optionname || product.dogma_name || 'Item');

                  if (isSummary) {
                    displayValue = fmt(product.dogma_amount ?? 0);
                    displayLabel = product.dogma_optionname || product.dogma_name || 'Item';
                  } else if (product.dogma_value) {
                    displayValue = product.dogma_value;
                    displayLabel = isMeasurementCategory
                      ? (product.dogma_name || 'Item')
                      : optionPlusFieldLabel;
                  } else {
                    displayValue = parseValue(product.dogma_value, product.dogma_name || 'Item');
                    displayLabel = isMeasurementCategory
                      ? (product.dogma_optionname || product.dogma_name || 'Item')
                      : optionPlusFieldLabel;
                  }

                  if (frameLabel) {
                    displayLabel = `${frameLabel} - ${displayLabel}`;
                  }

                  const isDimensionField = typeof displayValue === 'object';

                  if (isDimensionField) {
                    return (
                      <div key={rowKey} className="py-1 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-left text-gray-600 font-medium">
                            {displayLabel}:
                          </span>
                        </div>
                        <div className="ml-2">
                          {displayValue}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={rowKey} className="flex justify-between items-start py-1 border-b border-gray-100 last:border-b-0">
                      <span className="text-xs text-left text-gray-600 flex-shrink-0 pr-3">
                        {displayLabel}
                      </span>
                      <span className={`text-xs font-semibold text-right ${isSummary ? 'text-gray-900' : 'text-gray-900'}`}>
                        {displayValue}
                      </span>
                    </div>
                  );
                }

                // Multiple products sharing the same option name, step and
                // frame — a multi-select answer (several checkboxes chosen at
                // once), e.g. "Add Ons": Cill, Left Jamb, Head, Right Jamb.
                // Each product IS a selected option (dogma_name is the
                // answer itself, no separate dogma_value), so combine into
                // one row instead of repeating the group label once per item.
                const optionGroupLabel = firstProduct.dogma_optionname || firstProduct.dogma_name || 'Item';
                const hasAnyValue = optionProducts.some(p => p.dogma_value?.trim());
                if (!hasAnyValue) {
                  const combinedLabel = frameLabel ? `${frameLabel} - ${optionGroupLabel}` : optionGroupLabel;
                  const combinedValue = optionProducts.map(p => p.dogma_name).filter(Boolean).join(', ');
                  return (
                    <div key={rowKey} className="flex justify-between items-start py-1 border-b border-gray-100 last:border-b-0">
                      <span className="text-xs text-left text-gray-600 flex-shrink-0 pr-3">
                        {combinedLabel}
                      </span>
                      <span className="text-xs font-semibold text-gray-900 text-right">
                        {combinedValue}
                      </span>
                    </div>
                  );
                }
                return (
                  <React.Fragment key={rowKey}>
                    {optionProducts.map((product, idx) => {
                      const displayValue = product.dogma_value?.trim() || product.dogma_name || 'Item';
                      const baseLabel = product.dogma_name || product.dogma_optionname || 'Item';
                      const displayLabel = frameLabel ? `${frameLabel} - ${baseLabel}` : baseLabel;
                      return (
                        <div key={`${rowKey}-${idx}`} className="flex justify-between items-start py-1 border-b border-gray-100 last:border-b-0">
                          <span className="text-xs text-left text-gray-600 flex-shrink-0 pr-3">
                            {displayLabel}
                          </span>
                          <span className="text-xs font-semibold text-gray-900 text-right">
                            {displayValue}
                          </span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
